import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PlatformFooter from '../../components/PlatformFooter';
import { QRCodeCanvas } from 'qrcode.react';
import EticketScanner from './EticketScanner';
import EticketChannelManager from './EticketChannelManager';
import EticketBokunConnector from './EticketBokunConnector';
import LegalReacceptGate from './LegalReacceptGate';
import BookingEngineSettings from './BookingEngineSettings';
import { VerificationPanel, ActiveFilterSummary, SupplierInfoSettings } from './SalesVerification';
import { toast } from 'sonner';
import {
  Ticket, LayoutDashboard, ListChecks, Tags, LinkIcon, BarChart3, Settings as SettingsIcon,
  ScanLine, LogOut, Loader2, Plus, Copy, CheckCircle2, ExternalLink, Trash2, Send,
  Menu, X, Building2, Download, ArrowLeft, Users, Mail, KeyRound, FileSpreadsheet, FileText,
  User, Image as ImageIcon, ChevronDown, LayoutGrid, Sun, Moon, Radio, Plug, RefreshCw, Upload, Undo2,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const navy = '#0C2159';

const AVATAR_OPTIONS = [
  { id: 'cat', emoji: '🐱', label: 'Cat' }, { id: 'dog', emoji: '🐶', label: 'Dog' },
  { id: 'bear', emoji: '🐻', label: 'Bear' }, { id: 'panda', emoji: '🐼', label: 'Panda' },
  { id: 'rabbit', emoji: '🐰', label: 'Rabbit' }, { id: 'hamster', emoji: '🐹', label: 'Hamster' },
  { id: 'fox', emoji: '🦊', label: 'Fox' }, { id: 'koala', emoji: '🐨', label: 'Koala' },
  { id: 'lion', emoji: '🦁', label: 'Lion' }, { id: 'tiger', emoji: '🐯', label: 'Tiger' },
  { id: 'owl', emoji: '🦉', label: 'Owl' }, { id: 'penguin', emoji: '🐧', label: 'Penguin' },
  { id: 'unicorn', emoji: '🦄', label: 'Unicorn' }, { id: 'alien', emoji: '👽', label: 'Alien' },
];

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
    <rect width="32" height="32" rx="8" fill="#25D366" />
    <path fill="#fff" d="M16 6.5c-5.2 0-9.4 4.2-9.4 9.4 0 1.7.4 3.3 1.3 4.7L6.5 25.5l5-1.3c1.4.8 2.9 1.2 4.5 1.2 5.2 0 9.4-4.2 9.4-9.4S21.2 6.5 16 6.5zm0 17.1c-1.4 0-2.8-.4-4-1.1l-.3-.2-3 .8.8-2.9-.2-.3c-.8-1.2-1.2-2.7-1.2-4.1 0-4.3 3.5-7.8 7.8-7.8s7.8 3.5 7.8 7.8-3.5 7.8-7.8 7.8zm4.3-5.8c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.5.1-.2.2-.6.8-.7.9-.1.2-.3.2-.5.1-.2-.1-1-.4-1.9-1.2-.7-.6-1.2-1.4-1.3-1.6-.1-.2 0-.4.1-.5.1-.1.2-.3.4-.4.1-.1.2-.2.2-.4.1-.2 0-.3 0-.4 0-.1-.5-1.3-.7-1.8-.2-.4-.4-.4-.5-.4h-.5c-.2 0-.4.1-.6.3-.2.2-.8.8-.8 1.9 0 1.1.8 2.2.9 2.4.1.2 1.6 2.5 3.9 3.5.5.2 1 .4 1.3.5.5.2 1 .1 1.4.1.4-.1 1.4-.6 1.6-1.1.2-.5.2-1 .1-1.1-.1-.1-.2-.1-.4-.2z" />
  </svg>
);

const waDigits = (phone) => {
  let d = (phone || '').replace(/\D/g, '');
  if (d.startsWith('0')) d = '60' + d.slice(1);
  return d;
};
const shareTicketWhatsApp = (t) => {
  const link = `${window.location.origin}/eticket/ticket/${t.qr_token}`;
  const msg = `🎟️ Your StayMii e-ticket for ${t.company_name || ''}:\n${t.rate_name} — ${link}\n\nPresent the QR at the entrance for admission.`;
  const num = waDigits(t.phone);
  const base = num ? `https://wa.me/${num}` : 'https://wa.me/';
  window.open(`${base}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
};

const NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'tickets', label: 'Tickets', icon: ListChecks },
  { key: 'rates', label: 'Products', icon: Tags },
  { key: 'booking', label: 'Booking Link', icon: LinkIcon },
  { key: 'channel', label: 'Distribution', icon: Radio },
  { key: 'bokun', label: 'SGMC Connect', icon: Plug },
  { key: 'reports', label: 'Reports', icon: BarChart3 },
];

export default function EticketAdmin() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(undefined); // undefined=loading, null=needs onboarding
  const [view, setView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const emailKey = (() => { try { return JSON.parse(localStorage.getItem('unified_user') || '{}').email || ''; } catch { return ''; } })();
  const [selectedAvatar, setSelectedAvatar] = useState(() => localStorage.getItem(`staymii_avatar_${emailKey}`) || null);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [tempAvatar, setTempAvatar] = useState(null);
  const [isDark, setIsDark] = useState(() => localStorage.getItem('eticket_theme') === 'dark');
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const toggleDark = () => setIsDark(d => { localStorage.setItem('eticket_theme', !d ? 'dark' : 'light'); return !d; });

  const openAvatarModal = () => { setTempAvatar(selectedAvatar); setAvatarModalOpen(true); };
  const saveAvatar = () => {
    if (tempAvatar) { localStorage.setItem(`staymii_avatar_${emailKey}`, tempAvatar); setSelectedAvatar(tempAvatar); }
    else { localStorage.removeItem(`staymii_avatar_${emailKey}`); setSelectedAvatar(null); }
    setAvatarModalOpen(false);
  };

  const loadCompany = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/eticket/company/me`);
      setCompany(data.company || null);
    } catch { setCompany(null); }
  }, []);
  useEffect(() => { loadCompany(); }, [loadCompany]);

  const signOut = () => {
    ['unified_token', 'unified_user', 'token', 'einvoice_token', 'user'].forEach(k => localStorage.removeItem(k));
    navigate('/auth/login');
  };

  if (company === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-[#0C2159]" /></div>;
  }
  if (company === null) {
    return <CompanyOnboarding onCreated={loadCompany} onSignOut={signOut} />;
  }

  const me = JSON.parse(localStorage.getItem('unified_user') || '{}');
  const initials = String(me.full_name || me.name || me.email || 'U').trim().charAt(0).toUpperCase();
  const isOwner = company.owner_user_id === me.id;
  const navItems = NAV;
  const activeView = (!isOwner && view === 'settings') ? 'dashboard' : view;

  return (
    <div className={`min-h-screen flex ${isDark ? 'dark bg-[#0B1020]' : 'bg-slate-50'}`} data-testid="eticket-admin">
      <LegalReacceptGate />
      {/* Sidebar */}
      <aside className={`fixed md:static z-40 inset-y-0 left-0 w-64 bg-[#0C2159] text-white flex flex-col transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <div className="p-5 border-b border-white/10 flex items-center gap-2">
          <Ticket className="w-6 h-6 text-cyan-300" />
          <div>
            <p className="text-xs tracking-widest uppercase text-cyan-300">StayMii</p>
            <h1 className="font-bold leading-tight">E-Ticket</h1>
          </div>
        </div>
        <div className="px-4 py-3 border-b border-white/10">
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Company</p>
          <p className="font-semibold text-sm truncate" data-testid="sidebar-company">{company.name}</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map(n => {
            const Icon = n.icon;
            return (
              <button key={n.key} onClick={() => { setView(n.key); setSidebarOpen(false); }} data-testid={`nav-${n.key}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${view === n.key ? 'bg-cyan-500 text-[#0C2159]' : 'text-slate-200 hover:bg-white/10'}`}>
                <Icon className="w-4 h-4" /> {n.label}
              </button>
            );
          })}
          <button onClick={() => { setView('scanner'); setSidebarOpen(false); }} data-testid="nav-scanner"
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mt-2 ${view === 'scanner' ? 'bg-cyan-500 text-[#0C2159]' : 'text-slate-200 hover:bg-white/10'}`}>
            <ScanLine className="w-4 h-4" /> QR Scanner
          </button>
          {isOwner && (
            <button onClick={() => { setView('settings'); setSidebarOpen(false); }} data-testid="nav-settings"
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${view === 'settings' ? 'bg-cyan-500 text-[#0C2159]' : 'text-slate-200 hover:bg-white/10'}`}>
              <SettingsIcon className="w-4 h-4" /> Settings
            </button>
          )}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <button onClick={toggleDark} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/10" data-testid="eticket-theme-toggle">
            {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />} {isDark ? 'Light Mode' : 'Dark Mode'}
          </button>
          <button onClick={() => navigate('/module-selector')} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/10" data-testid="back-to-modules">
            <LayoutGrid className="w-4 h-4" /> Switch Module
          </button>
          <button onClick={signOut} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/10" data-testid="sign-out">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col min-h-screen">
        <header className="bg-[#0C2159] h-[60px] flex items-center justify-between px-4 md:px-6 shrink-0 border-b border-white/10 sticky top-0 z-20" data-testid="eticket-topbar">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-300 hover:text-white" data-testid="open-sidebar">
              <Menu className="w-6 h-6" />
            </button>
            <div className="w-8 h-8 rounded-lg bg-cyan-500 flex items-center justify-center shrink-0">
              <Ticket className="w-4 h-4 text-[#0C2159]" />
            </div>
            <span className="text-white font-bold text-lg">StayMii <span className="text-cyan-400">E-Ticket</span></span>
          </div>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:bg-white/10 rounded-lg px-2 py-1.5 transition-colors" data-testid="eticket-user-profile-btn">
                  <div className="w-9 h-9 rounded-full bg-cyan-100 flex items-center justify-center overflow-hidden shrink-0">
                    {selectedAvatar
                      ? <span className="text-lg">{(AVATAR_OPTIONS.find(a => a.id === selectedAvatar) || {}).emoji}</span>
                      : <span className="font-bold text-[#0C2159] text-sm">{initials}</span>}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-white truncate max-w-[150px]">{me.full_name || me.name || 'User'}</p>
                    <p className="text-xs text-cyan-200 truncate max-w-[150px]">{isOwner ? 'Owner' : 'Staff'}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-cyan-200 hidden md:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b">
                  <p className="text-sm font-medium text-slate-900">{me.full_name || me.name || 'User'}</p>
                  <p className="text-xs text-slate-500 truncate">{me.email || ''}</p>
                </div>
                <DropdownMenuItem onClick={() => setProfileModalOpen(true)} className="cursor-pointer" data-testid="eticket-menu-profile">
                  <User className="h-4 w-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openAvatarModal} className="cursor-pointer" data-testid="eticket-menu-avatar">
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Change Avatar
                </DropdownMenuItem>
                {isOwner && (
                  <DropdownMenuItem onClick={() => setView('settings')} className="cursor-pointer" data-testid="eticket-menu-settings">
                    <SettingsIcon className="h-4 w-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="cursor-pointer text-red-600 focus:text-red-600" data-testid="eticket-menu-logout">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="eticket-main flex-1 w-full p-5 md:p-8 max-w-6xl mx-auto">
          {activeView === 'dashboard' && <DashboardView />}
          {activeView === 'tickets' && <TicketsView company={company} />}
          {activeView === 'rates' && <RatesView />}
          {activeView === 'booking' && <BookingLinkView company={company} />}
          {activeView === 'channel' && <EticketChannelManager onOpenProviderTab={() => setView('bokun')} />}
          {activeView === 'bokun' && <EticketBokunConnector />}
          {activeView === 'reports' && <ReportsView />}
          {activeView === 'settings' && <SettingsView company={company} onSaved={loadCompany} />}
          {activeView === 'scanner' && <EticketScanner embedded />}
        </main>
        <PlatformFooter className="bg-[#0C2159]" />
      </div>

      <Dialog open={profileModalOpen} onOpenChange={setProfileModalOpen}>
        <DialogContent className="w-[90vw] max-w-sm mx-auto" data-testid="eticket-profile-modal">
          <DialogHeader>
            <DialogTitle>My Profile</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="w-20 h-20 rounded-full bg-cyan-100 flex items-center justify-center overflow-hidden">
              {selectedAvatar
                ? <span className="text-4xl">{(AVATAR_OPTIONS.find(a => a.id === selectedAvatar) || {}).emoji}</span>
                : <span className="font-bold text-[#0C2159] text-2xl">{initials}</span>}
            </div>
            <div className="text-center">
              <p className="font-semibold text-slate-900" data-testid="eticket-profile-name">{me.full_name || me.name || 'User'}</p>
              <p className="text-sm text-slate-500" data-testid="eticket-profile-email">{me.email || ''}</p>
              <p className="text-xs text-slate-400 mt-1">{isOwner ? 'Owner' : 'Staff'} · {company.name}</p>
            </div>
            <button onClick={() => { setProfileModalOpen(false); openAvatarModal(); }} className="mt-1 text-sm text-cyan-700 hover:underline" data-testid="eticket-profile-change-avatar">Change avatar</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={avatarModalOpen} onOpenChange={setAvatarModalOpen}>
        <DialogContent className="w-[90vw] max-w-md mx-auto" data-testid="eticket-avatar-modal">
          <DialogHeader>
            <DialogTitle>Choose Your Avatar</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2 md:gap-3 py-4">
            {AVATAR_OPTIONS.map((a) => (
              <button
                key={a.id}
                onClick={() => setTempAvatar(a.id)}
                className={`h-12 w-12 md:h-16 md:w-16 rounded-xl flex items-center justify-center text-2xl md:text-3xl transition-all hover:scale-110 ${tempAvatar === a.id ? 'bg-cyan-100 ring-2 ring-cyan-500' : 'bg-slate-100 hover:bg-slate-200'}`}
                title={a.label}
                data-testid={`eticket-avatar-option-${a.id}`}
              >
                {a.emoji}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3">
            <button onClick={() => setTempAvatar(null)} className="text-sm text-slate-500 hover:text-slate-700" data-testid="eticket-avatar-clear">Use initials</button>
            <div className="flex gap-2">
              <button onClick={() => setAvatarModalOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button onClick={saveAvatar} className="px-4 py-2 text-sm rounded-lg bg-[#0C2159] text-white hover:opacity-90" data-testid="eticket-avatar-save">Save</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Onboarding ---------------- */
function CompanyOnboarding({ onCreated, onSignOut }) {
  const [f, setF] = useState({ name: '', registration_number: '', contact_number: '', email: '', address: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setErr('');
    if (!f.name.trim()) { setErr('Company name is required.'); return; }
    setBusy(true);
    try { await axios.post(`${API}/api/eticket/company`, f); onCreated(); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not create company.'); setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0C2159] flex items-center justify-center p-4" data-testid="eticket-onboarding">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-7">
        <div className="flex items-center gap-2 text-cyan-600"><Building2 className="w-5 h-5" /><span className="text-xs tracking-widest uppercase font-semibold">StayMii E-Ticket</span></div>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">Set up your company</h1>
        <p className="text-sm text-slate-500 mt-1">Create your ticketing workspace. You can edit these details later in Settings.</p>
        <div className="mt-5 space-y-3">
          <Inp label="Company name *" v={f.name} on={e => setF({ ...f, name: e.target.value })} testid="onb-name" />
          <Inp label="Registration number" v={f.registration_number} on={e => setF({ ...f, registration_number: e.target.value })} testid="onb-reg" />
          <div className="grid grid-cols-2 gap-3">
            <Inp label="Contact number" v={f.contact_number} on={e => setF({ ...f, contact_number: e.target.value })} testid="onb-contact" />
            <Inp label="Email" v={f.email} on={e => setF({ ...f, email: e.target.value })} testid="onb-email" />
          </div>
          <Inp label="Address" v={f.address} on={e => setF({ ...f, address: e.target.value })} testid="onb-address" />
        </div>
        {err && <p className="text-sm text-red-600 mt-3" data-testid="onb-error">{err}</p>}
        <button onClick={submit} disabled={busy} data-testid="onb-submit"
          className="w-full mt-5 bg-[#0C2159] hover:bg-[#13317a] text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />} Create company
        </button>
        <button onClick={onSignOut} className="w-full mt-3 text-slate-400 hover:text-slate-600 text-sm">Sign out</button>
      </div>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function SgmcFeed() {
  const [events, setEvents] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/api/bokun/cm/admin/feed?limit=15`)
      .then(r => setEvents(r.data.events || [])).catch(() => setEvents([]));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  const timeAgo = (iso) => {
    if (!iso) return '';
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };
  const PROVIDER_LOGOS = {
    bokun: '/ota-logos/bokun.png',
    globaltix: '/ota-logos/globaltix.png',
    staymii: '/favicon-48x48.png',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-6" data-testid="sgmc-feed">
      <div className="flex items-center justify-between mb-3">
        <p className="font-semibold text-slate-900 flex items-center gap-2"><Plug className="w-4 h-4 text-[#0C2159]" /> SGMC Live Feed
          <span className="text-xs font-normal text-slate-400">incoming StayMii / Bókun / GlobalTix bookings & cancellations</span>
        </p>
        <button onClick={load} className="text-slate-400 hover:text-[#0C2159]" data-testid="sgmc-feed-refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {!events ? <Loading /> : events.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center" data-testid="sgmc-feed-empty">No bookings yet. Bookings from StayMii, Bókun & GlobalTix will stream in here.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {events.map((e, i) => (
            <div key={i} className="flex items-center gap-3 py-2.5" data-testid={`sgmc-feed-item-${i}`}>
              <div className="relative shrink-0">
                <div className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
                  <img src={PROVIDER_LOGOS[e.provider] || '/favicon-48x48.png'} alt={e.provider_label}
                    className="w-full h-full object-contain p-0.5"
                    onError={(ev) => { ev.currentTarget.style.display = 'none'; }} />
                </div>
                <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ring-2 ring-white ${e.type === 'cancellation' ? 'bg-red-500' : 'bg-emerald-500'}`}>
                  {e.type === 'cancellation'
                    ? <Trash2 className="w-2.5 h-2.5 text-white" />
                    : <CheckCircle2 className="w-2.5 h-2.5 text-white" />}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 truncate">
                  <span className="font-semibold">{e.provider_label}</span> {e.type === 'cancellation' ? 'cancellation' : 'booking'} · {e.guest}
                </p>
                <p className="text-xs text-slate-400">{e.tickets} ticket(s) · visit {e.visit_date} · {e.booking_code}</p>
              </div>
              <span className="text-xs text-slate-400 shrink-0">{timeAgo(e.at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DashboardView() {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/api/eticket/dashboard`).then(r => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Loading />;
  const cur = d.currency || 'MYR';
  const cards = [
    { label: 'Total Bookings', value: d.total_bookings, testid: 'stat-bookings' },
    { label: 'Total Revenue', value: `${cur} ${Number(d.total_revenue).toFixed(2)}`, testid: 'stat-revenue' },
    { label: "Today's Visitors", value: d.today_visitors, testid: 'stat-visitors' },
    { label: "Today's Ticket Sales", value: d.today_ticket_sales, testid: 'stat-today-sales' },
  ];
  return (
    <div data-testid="dashboard-view">
      <H title="Dashboard" sub="Overview of your ticketing operations" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => (
          <div key={c.label} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={c.testid}>
            <p className="text-3xl font-bold text-[#0C2159]">{c.value}</p>
            <p className="text-sm text-slate-500 mt-1">{c.label}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-4">Tickets sold (paid): {d.total_tickets_sold}</p>
      <SgmcFeed />
    </div>
  );
}

/* ---------------- Tickets ---------------- */
const QR_STATUS = {
  valid: { label: 'Not Redeemed', cls: 'bg-amber-100 text-amber-700' },
  admitted: { label: 'Redeemed', cls: 'bg-emerald-100 text-emerald-700' },
  pending_payment: { label: 'Unpaid', cls: 'bg-slate-100 text-slate-500' },
  cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-700' },
};
const fmtMoney = (v, cur) => `${(cur || 'MYR').toUpperCase()} ${Number(v || 0).toFixed(2)}`;

const REDEEM_STAFF = ['Napy', 'Loga'];
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

function RedeemDialog({ ticket, busy, onClose, onSubmit }) {
  const [staff, setStaff] = useState(REDEEM_STAFF[0]);
  const [when, setWhen] = useState('');
  useEffect(() => { if (ticket) { setStaff(REDEEM_STAFF[0]); setWhen(toLocalInput(ticket.booking_date)); } }, [ticket]);
  if (!ticket) return null;
  const unpaid = ticket.status === 'pending_payment';
  const submit = () => onSubmit(ticket, { scanned_by: staff, admitted_at: new Date(when).toISOString(), mark_paid: unpaid });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="redeem-dialog">
        <DialogHeader><DialogTitle>Redeem {ticket.ticket_number}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{ticket.holder_name} · {ticket.rate_name}</p>
        <label className="block text-xs uppercase tracking-wide text-slate-400 mt-2">Admission by</label>
        <div className="flex gap-2" data-testid="redeem-staff-options">
          {REDEEM_STAFF.map(s => (
            <button key={s} onClick={() => setStaff(s)} data-testid={`redeem-staff-${s.toLowerCase()}`}
              className={`flex-1 py-2 rounded-lg border text-sm font-semibold ${staff === s ? 'bg-[#0C2159] text-white border-[#0C2159]' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>{s}</button>
          ))}
        </div>
        <label className="block text-xs uppercase tracking-wide text-slate-400 mt-2">Admission date &amp; time (defaults to purchase date)</label>
        <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} data-testid="redeem-admitted-at"
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm w-full" />
        {unpaid && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="redeem-unpaid-note">This ticket is unpaid — redeeming will also mark it as Paid.</p>}
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500" data-testid="redeem-cancel">Cancel</button>
          <button onClick={submit} disabled={busy || !when} data-testid="redeem-confirm"
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">Redeem</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TicketSalesSummary({ rows }) {
  const paid = rows.filter(t => t.payment_status === 'paid' && !t.demo);
  const cur = (rows[0]?.currency || 'MYR');
  const byType = {};
  paid.forEach(t => {
    const k = t.rate_name || 'Admission';
    byType[k] = byType[k] || { qty: 0, free: 0, revenue: 0 };
    byType[k].qty += 1;
    if (t.is_promo_free) byType[k].free += 1;
    byType[k].revenue += Number(t.amount || 0);
  });
  const total = paid.reduce((s, t) => s + Number(t.amount || 0), 0);
  const types = Object.entries(byType).sort((a, b) => b[1].qty - a[1].qty);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4" data-testid="ticket-sales-summary">
      <div className="flex items-baseline justify-between mb-3">
        <p className="text-[11px] uppercase tracking-wide text-slate-400">Tickets sold by type (paid, excl. demo)</p>
        <p className="text-sm text-slate-500" data-testid="ticket-sales-total">
          <span className="font-bold text-[#0C2159]">{paid.length}</span> sold · <span className="font-bold text-[#0C2159]">{fmtMoney(total, cur)}</span>
        </p>
      </div>
      {types.length === 0 ? <p className="text-sm text-slate-400">No paid tickets in this view yet.</p> : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {types.map(([name, v]) => (
            <div key={name} className="rounded-xl bg-slate-50 px-3 py-2.5" data-testid={`ticket-sales-type-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <p className="text-xs text-slate-500 truncate" title={name}>{name}</p>
              <p className="text-lg font-bold text-[#0C2159]">{v.qty} <span className="text-xs font-medium text-slate-400">sold{v.free ? ` (${v.free} free)` : ''}</span></p>
              <p className="text-xs font-semibold text-emerald-700">{fmtMoney(v.revenue, cur)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_FILTERS = { channel: 'all', payment_status: 'all', rate_name: '', date_from: '', date_to: '', purchase_from: '', purchase_to: '' };
const PAGE_SIZE = 25;
const PAY_CLS = { paid: 'bg-emerald-100 text-emerald-700', refunded: 'bg-red-100 text-red-700', failed: 'bg-red-100 text-red-700' };

function TicketsView({ company }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');
  const [opts, setOpts] = useState({ channels: [], rate_names: [] });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  const channelLabel = (c) => {
    if (c === 'direct' || !c) return 'Own Website Booking';
    if (c === 'bokun') return 'Bókun';
    if (c === 'globaltix') return 'GlobalTix';
    return c;
  };

  const load = useCallback(() => {
    const params = {};
    if (filters.channel && filters.channel !== 'all') params.channel = filters.channel;
    if (filters.rate_name) params.rate_name = filters.rate_name;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.payment_status !== 'all') params.payment_status = filters.payment_status;
    if (filters.purchase_from) params.purchase_from = filters.purchase_from;
    if (filters.purchase_to) params.purchase_to = filters.purchase_to;
    setPage(0);
    setReloadKey(k => k + 1);
    return axios.get(`${API}/api/eticket/tickets`, { params }).then(r => setRows(r.data)).catch(() => setRows([]));
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/api/eticket/tickets/filters`).then(r => setOpts(r.data)).catch(() => {});
  }, []);

  const resend = async (id) => {
    setBusy(id);
    try { const { data } = await axios.post(`${API}/api/eticket/ticket/${id}/resend`); alert(data.sent ? 'Ticket resent.' : 'Could not send (no email or send failed).'); }
    catch (e) { alert(e.response?.data?.detail || 'Resend failed.'); }
    finally { setBusy(''); }
  };
  const [redeemFor, setRedeemFor] = useState(null);
  const redeem = (t) => setRedeemFor(t);
  const submitRedeem = async (t, body) => {
    setBusy(t.id);
    try { await axios.post(`${API}/api/eticket/ticket/${t.id}/redeem`, body); toast.success('Ticket redeemed.'); setRedeemFor(null); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not redeem ticket.'); }
    finally { setBusy(''); }
  };
  const unredeem = async (t) => {
    if (!window.confirm(`Undo redemption for ticket ${t.ticket_number}? It will become valid again.`)) return;
    setBusy(t.id);
    try { await axios.post(`${API}/api/eticket/ticket/${t.id}/unredeem`); toast.success('Redemption reversed.'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not undo redemption.'); }
    finally { setBusy(''); }
  };
  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const active = Object.keys(EMPTY_FILTERS).some(k => filters[k] !== EMPTY_FILTERS[k]);
  const pages = rows ? Math.max(1, Math.ceil(rows.length / PAGE_SIZE)) : 1;
  const pageRows = rows ? rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE) : [];
  const dateInp = (key, label) => (
    <div>
      <label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">{label}</label>
      <input type="date" value={filters[key]} onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
        className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid={`ticket-filter-${key.replace('_', '-')}`} />
    </div>
  );

  return (
    <div data-testid="tickets-view">
      <H title="Tickets" sub="All ticket bookings — direct sales and SGMC channels (Viator / GetYourGuide / Klook)" />

      <VerificationPanel filters={filters} reloadKey={reloadKey} />

      {/* Filter bar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-2 flex flex-wrap items-end gap-3" data-testid="tickets-filter-bar">
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Attraction</label>
          <div className="px-3 py-2 border border-slate-200 bg-slate-50 rounded-lg text-sm text-slate-600 max-w-[220px] truncate" data-testid="ticket-filter-attraction">{company?.supplier_info?.attraction_name || company?.name}</div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Payment status</label>
          <select value={filters.payment_status} onChange={e => setFilters(f => ({ ...f, payment_status: e.target.value }))}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="ticket-filter-payment-status">
            <option value="all">All</option><option value="paid">Paid</option><option value="pending">Pending</option>
            <option value="failed">Failed</option><option value="refunded">Refunded</option><option value="cancelled">Cancelled</option>
          </select>
        </div>
        {dateInp('purchase_from', 'Purchased from')}
        {dateInp('purchase_to', 'Purchased to')}
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Channel</label>
          <select value={filters.channel} onChange={e => setFilters(f => ({ ...f, channel: e.target.value }))}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white min-w-[150px]" data-testid="ticket-filter-channel">
            <option value="all">All channels</option>
            <option value="direct">Own Website Booking</option>
            <option value="bokun">Bókun</option>
            <option value="globaltix">GlobalTix</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Ticket type</label>
          <select value={filters.rate_name} onChange={e => setFilters(f => ({ ...f, rate_name: e.target.value }))}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white min-w-[160px]" data-testid="ticket-filter-type">
            <option value="">All types</option>
            {opts.rate_names.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Visit from</label>
          <input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="ticket-filter-date-from" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">Visit to</label>
          <input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="ticket-filter-date-to" />
        </div>
        {active && (
          <button onClick={clearFilters} className="px-3 py-2 text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1.5" data-testid="ticket-filter-clear">
            <X className="w-4 h-4" /> Reset filters
          </button>
        )}
        <span className="ml-auto text-sm text-slate-400 self-center" data-testid="tickets-count">{rows ? `${rows.length} ticket(s)${rows.some(r => r.demo) ? ` · ${rows.filter(r => r.demo).length} demo` : ''}` : ''}</span>
      </div>
      <ActiveFilterSummary company={company} f={filters} />

      {rows && rows.length > 0 && <TicketSalesSummary rows={rows} />}
      <RedeemDialog ticket={redeemFor} busy={!!busy} onClose={() => setRedeemFor(null)} onSubmit={submitRedeem} />

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="tickets-table">
        {!rows ? <div className="p-8"><Loading /></div> : rows.length === 0 ? <Empty text="No tickets match these filters." /> : (
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left p-3">Booking ID</th><th className="text-left p-3">Channel</th>
                <th className="text-left p-3">Visitor</th>
                <th className="text-left p-3">Type</th><th className="text-left p-3">Visit</th>
                <th className="text-center p-3">QR Status</th><th className="text-left p-3">Scanned By</th>
                <th className="text-left p-3">Admission</th><th className="text-center p-3">Qty</th><th className="text-right p-3">Amount</th><th className="text-center p-3">Payment</th>
                <th className="text-left p-3">Purchase Date</th><th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(t => {
                const st = QR_STATUS[t.status] || QR_STATUS.valid;
                const isOta = t.channel && t.channel !== 'direct';
                return (
                  <tr key={t.qr_token} className="border-t border-slate-100" data-testid={`ticket-row-${t.ticket_number}`}>
                    <td className="p-3 font-mono text-xs">{t.ticket_number}{t.demo ? <span className="block mt-0.5 w-fit px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[9px] font-sans font-bold" data-testid={`ticket-demo-${t.ticket_number}`}>DEMO</span> : null}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${isOta ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600'}`} data-testid={`ticket-channel-${t.ticket_number}`}>
                        {channelLabel(t.channel)}
                      </span>
                      {isOta && t.ota_source ? <span className="block text-[10px] text-slate-400 mt-0.5">{t.ota_source}</span> : null}
                    </td>
                    <td className="p-3">
                      {t.holder_name}
                    </td>
                    <td className="p-3">{t.rate_name}</td>
                    <td className="p-3">{t.visit_date || '—'}</td>
                    <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.cls}`}>{st.label}</span></td>
                    <td className="p-3">{t.scanned_by || '—'}</td>
                    <td className="p-3 text-xs">{t.admitted_at ? new Date(t.admitted_at).toLocaleString() : '—'}</td>
                    <td className="p-3 text-center" data-testid={`ticket-qty-${t.ticket_number}`}>{t.quantity || 1}</td>
                    <td className="p-3 text-right whitespace-nowrap font-semibold text-[#0C2159]" data-testid={`ticket-amount-${t.ticket_number}`}>
                      {fmtMoney(t.amount, t.currency)}
                      {t.is_promo_free ? <span className="block text-[10px] font-semibold text-emerald-600">FREE (promo)</span> : null}
                      {t.refund_amount > 0 ? <span className="block text-[10px] font-semibold text-red-600">Refund −{Number(t.refund_amount).toFixed(2)} · Net {(t.amount - t.refund_amount).toFixed(2)}</span> : null}
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${PAY_CLS[t.payment_status] || 'bg-slate-100 text-slate-500'}`} data-testid={`ticket-payment-${t.ticket_number}`}>{t.payment_status}</span>
                      {t.payment_reference ? <span className="block text-[10px] font-mono text-slate-400 mt-0.5 max-w-[110px] truncate mx-auto" title={t.payment_reference}>{t.payment_reference}</span> : null}
                    </td>
                    <td className="p-3 text-xs" data-testid={`ticket-purchase-date-${t.ticket_number}`}>{t.purchase_date || '—'}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2">
                        {(t.status === 'valid' || t.status === 'pending_payment') && (
                          <button onClick={() => redeem(t)} disabled={busy === t.id} title="Mark as redeemed (admit guest)" data-testid={`ticket-redeem-${t.ticket_number}`} className="text-emerald-600 hover:text-emerald-800 disabled:opacity-30">
                            {busy === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                          </button>
                        )}
                        {t.status === 'admitted' && (
                          <button onClick={() => unredeem(t)} disabled={busy === t.id} title="Undo redemption" data-testid={`ticket-unredeem-${t.ticket_number}`} className="text-slate-400 hover:text-slate-700 disabled:opacity-30">
                            {busy === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                          </button>
                        )}
                        <a href={`/eticket/ticket/${t.qr_token}`} target="_blank" rel="noreferrer" title="View / Print" data-testid={`ticket-view-${t.ticket_number}`} className="text-cyan-700 hover:text-cyan-900"><ExternalLink className="w-4 h-4" /></a>
                        <button onClick={() => shareTicketWhatsApp(t)} title="Share to WhatsApp" data-testid={`ticket-whatsapp-${t.ticket_number}`} className="hover:opacity-80 transition-opacity"><WhatsAppIcon className="w-5 h-5" /></button>
                        <button onClick={() => resend(t.id)} disabled={busy === t.id || t.payment_status !== 'paid'} title="Resend e-ticket" data-testid={`ticket-resend-${t.ticket_number}`} className="text-[#0C2159] hover:text-[#13317a] disabled:opacity-30">
                          {busy === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {rows && pages > 1 && (
        <div className="flex items-center justify-end gap-3 mt-3 text-sm" data-testid="tickets-pagination">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} data-testid="tickets-prev" className="px-3 py-1.5 rounded-lg border border-slate-300 disabled:opacity-40">Previous</button>
          <span className="text-slate-500" data-testid="tickets-page-indicator">Page {page + 1} of {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} data-testid="tickets-next" className="px-3 py-1.5 rounded-lg border border-slate-300 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

/* ---------------- Rates ---------------- */
function RatesView() {
  const [rates, setRates] = useState(null);
  const [f, setF] = useState({ name: '', price: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = () => axios.get(`${API}/api/eticket/rates`).then(r => setRates(r.data)).catch(() => setRates([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    setErr('');
    if (!f.name.trim() || f.price === '') { setErr('Name and price are required.'); return; }
    setBusy(true);
    try { await axios.post(`${API}/api/eticket/rates`, { name: f.name, price: parseFloat(f.price), active: true }); setF({ name: '', price: '' }); load(); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not create rate.'); }
    finally { setBusy(false); }
  };
  const toggle = async (r) => { await axios.put(`${API}/api/eticket/rates/${r.id}`, { active: !r.active }); load(); };
  const remove = async (r) => { if (window.confirm(`Delete rate "${r.name}"?`)) { await axios.delete(`${API}/api/eticket/rates/${r.id}`); load(); } };
  if (!rates) return <Loading />;
  return (
    <div data-testid="rates-view">
      <H title="Rates" sub="Create and manage your ticket prices" />
      <PromoPanel />
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 md:col-span-1" data-testid="rate-create-form">
          <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><Plus className="w-4 h-4" /> New Rate</h3>
          <div className="space-y-3">
            <Inp label="Ticket name *" v={f.name} on={e => setF({ ...f, name: e.target.value })} testid="rate-name" />
            <Inp label="Price *" type="number" v={f.price} on={e => setF({ ...f, price: e.target.value })} testid="rate-price" />
          </div>
          {err && <p className="text-sm text-red-600 mt-2" data-testid="rate-error">{err}</p>}
          <button onClick={create} disabled={busy} data-testid="rate-create-btn" className="w-full mt-4 bg-[#0C2159] hover:bg-[#13317a] text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Rate
          </button>
        </div>
        <div className="md:col-span-2 space-y-3" data-testid="rates-list">
          {rates.length === 0 ? <Empty text="No rates yet." /> : rates.map(r => (
            <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between" data-testid={`rate-row-${r.id}`}>
              <div>
                <p className="font-semibold text-slate-900">{r.name}</p>
                <p className="text-sm text-[#0C2159] font-bold">{(r.currency || 'MYR').toUpperCase()} {Number(r.price).toFixed(2)}</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => toggle(r)} data-testid={`rate-toggle-${r.id}`} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${r.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{r.active ? 'Active' : 'Inactive'}</button>
                <button onClick={() => remove(r)} data-testid={`rate-delete-${r.id}`} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Promotion (company-wide Buy X Get Y Free) ---------------- */
function PromoPanel() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    axios.get(`${API}/api/eticket/company/me`).then(r => {
      const promo = r.data?.company?.promo || {};
      setP({
        enabled: !!promo.enabled,
        name: promo.name || 'Buy 1 Free 1',
        buy_qty: promo.buy_qty || 1,
        free_qty: promo.free_qty || 1,
        start_date: promo.start_date || '',
        end_date: promo.end_date || '',
      });
    }).catch(() => setP({ enabled: false, name: 'Buy 1 Free 1', buy_qty: 1, free_qty: 1, start_date: '', end_date: '' }));
  }, []);
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      await axios.put(`${API}/api/eticket/company/promotion`, {
        enabled: p.enabled,
        name: p.name,
        buy_qty: parseInt(p.buy_qty || 1, 10),
        free_qty: parseInt(p.free_qty || 1, 10),
        start_date: p.start_date || '',
        end_date: p.end_date || '',
      });
      setMsg(p.enabled ? 'Promotion saved and LIVE on your booking page.' : 'Saved — but the promotion is OFF, so it will not appear. Turn it ON above and Save again.');
    } catch (e) { setMsg(e.response?.data?.detail || 'Could not save promotion.'); }
    finally { setBusy(false); }
  };
  if (!p) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="promo-panel">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><Tags className="w-4 h-4" /> Promotion</h3>
        <button type="button" onClick={() => setP({ ...p, enabled: !p.enabled })} data-testid="promo-enabled"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors ${p.enabled ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}>
          <span className={`w-2.5 h-2.5 rounded-full ${p.enabled ? 'bg-white' : 'bg-slate-400'}`} />
          {p.enabled ? 'Promotion ON' : 'Promotion OFF — click to turn on'}
        </button>
      </div>
      {!p.enabled && (
        <p className="text-xs font-semibold text-amber-600 mb-2" data-testid="promo-off-hint">⚠️ This promotion is OFF and will NOT show on your booking page. Click the toggle above to turn it ON, then Save.</p>
      )}
      <p className="text-xs text-slate-500 mb-4">Company-wide <strong>Buy X Get Y Free</strong> deal applied to every ticket type on your booking page. Example: Buy 1, Free 1 &rarr; the customer buys 1 ticket and gets 1 extra ticket free (2 tickets total, pays for 1).</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Inp label="Promotion name" v={p.name} on={e => setP({ ...p, name: e.target.value })} testid="promo-name" />
        <Inp label="Buy quantity *" type="number" v={p.buy_qty} on={e => setP({ ...p, buy_qty: e.target.value })} testid="promo-buy" />
        <Inp label="Free quantity *" type="number" v={p.free_qty} on={e => setP({ ...p, free_qty: e.target.value })} testid="promo-free" />
        <Inp label="Start date (optional)" type="date" v={p.start_date} on={e => setP({ ...p, start_date: e.target.value })} testid="promo-start" />
        <Inp label="End date (optional)" type="date" v={p.end_date} on={e => setP({ ...p, end_date: e.target.value })} testid="promo-end" />
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={save} disabled={busy} data-testid="promo-save-btn" className="bg-[#0C2159] hover:bg-[#13317a] text-white font-semibold py-2.5 px-5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save Promotion
        </button>
        {msg && <span className="text-sm text-slate-600" data-testid="promo-msg">{msg}</span>}
      </div>
    </div>
  );
}

/* ---------------- Booking Link ---------------- */
function BookingLinkView({ company }) {
  const staymiiUrl = `${window.location.origin}/eticket/book/${company.slug}`;
  const [source, setSource] = useState(company.preferred_booking_source || 'staymii');
  const [links, setLinks] = useState({
    bokun: company.booking_links?.bokun || '',
    globaltix: company.booking_links?.globaltix || '',
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const sources = [
    { key: 'staymii', label: 'StayMii', hint: 'Your native booking page' },
    { key: 'bokun', label: 'Bókun', hint: 'Bókun booking widget URL' },
    { key: 'globaltix', label: 'GlobalTix', hint: 'GlobalTix booking URL' },
  ];
  const activeUrl = source === 'staymii' ? staymiiUrl : (links[source] || '');

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/eticket/company/booking-links`,
        { booking_links: links, preferred_booking_source: source });
      toast.success('Booking link settings saved.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save booking links.');
    } finally { setSaving(false); }
  };
  const copy = async () => {
    if (!activeUrl) return;
    try { await navigator.clipboard.writeText(activeUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { window.prompt('Copy this booking link:', activeUrl); }
  };
  const downloadQR = () => {
    const canvas = document.querySelector('#booking-qr canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${company.slug}-booking-qr.png`;
    a.click();
  };

  return (
    <div data-testid="booking-link-view">
      <H title="Booking Link" sub="Choose which platform powers your public booking link" />
      <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-xl">
        <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Booking source</p>
        <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden mb-4" data-testid="booking-source-tabs">
          {sources.map(s => (
            <button key={s.key} onClick={() => setSource(s.key)} data-testid={`booking-source-${s.key}`}
              className={`px-4 py-2 text-sm font-medium ${source === s.key ? 'bg-[#0C2159] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
              {s.label}
            </button>
          ))}
        </div>

        {source === 'staymii' ? (
          <>
            <p className="text-xs uppercase tracking-wide text-slate-500">Public booking URL (StayMii)</p>
            <div className="mt-2 flex items-center gap-2">
              <input readOnly value={staymiiUrl} data-testid="booking-url" className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-50" />
            </div>
          </>
        ) : (
          <>
            <p className="text-xs uppercase tracking-wide text-slate-500">{sources.find(s => s.key === source).hint}</p>
            <input
              value={links[source]}
              onChange={e => setLinks(l => ({ ...l, [source]: e.target.value }))}
              placeholder={`https://…  (paste your ${source === 'bokun' ? 'Bókun' : 'GlobalTix'} booking link)`}
              data-testid={`booking-url-input-${source}`}
              className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            {source === 'globaltix' && (
              <p className="text-[11px] text-amber-600 mt-1">GlobalTix connection is pending API — you can still paste a link once available.</p>
            )}
          </>
        )}

        <div className="mt-3 flex items-center gap-2">
          <button onClick={copy} disabled={!activeUrl} data-testid="copy-booking-link"
            className="bg-[#0C2159] hover:bg-[#13317a] disabled:opacity-50 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1.5">
            {copied ? <><CheckCircle2 className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
          </button>
          {activeUrl ? (
            <a href={activeUrl} target="_blank" rel="noreferrer" className="bg-cyan-50 text-cyan-700 px-3 py-2 rounded-lg text-sm flex items-center gap-1.5"><ExternalLink className="w-4 h-4" /> Open</a>
          ) : null}
          <button onClick={save} disabled={saving} data-testid="save-booking-links"
            className="ml-auto bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save as primary
          </button>
        </div>

        {activeUrl ? (
          <div className="mt-6 flex flex-col items-center" id="booking-qr">
            <div className="p-4 bg-white rounded-xl border border-slate-200">
              <QRCodeCanvas value={activeUrl} size={200} fgColor={navy} level="H" data-testid="booking-qr-canvas" />
            </div>
            <button onClick={downloadQR} data-testid="download-qr" className="mt-4 inline-flex items-center gap-2 text-sm text-[#0C2159] font-medium hover:underline"><Download className="w-4 h-4" /> Download QR code</button>
          </div>
        ) : (
          <p className="mt-6 text-sm text-slate-400 text-center">Paste a link above to generate its QR code.</p>
        )}
      </div>
    </div>
  );
}

/* ---------------- Reports ---------------- */
function ReportsView() {
  const [period, setPeriod] = useState('daily');
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState('');
  useEffect(() => {
    setData(null);
    axios.get(`${API}/api/eticket/reports?period=${period}`).then(r => setData(r.data)).catch(() => setData({ rows: [], total_tickets: 0, total_revenue: 0, currency: 'MYR' }));
  }, [period]);
  const exportFile = async (format) => {
    setExporting(format);
    try {
      const res = await axios.get(`${API}/api/eticket/reports/export?period=${period}&format=${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `report-${period}.${format === 'pdf' ? 'pdf' : 'xlsx'}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Export failed.'); } finally { setExporting(''); }
  };
  return (
    <div data-testid="reports-view">
      <H title="Reports" sub="Sales by day, month or year" />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden" data-testid="report-period-tabs">
          {['daily', 'monthly', 'annual'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`period-${p}`}
              className={`px-4 py-2 text-sm font-medium capitalize ${period === p ? 'bg-[#0C2159] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>{p}</button>
          ))}
        </div>
        <div className="flex-1" />
        <button onClick={() => exportFile('xlsx')} disabled={exporting === 'xlsx'} data-testid="export-excel" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-xl disabled:opacity-60">
          {exporting === 'xlsx' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Excel
        </button>
        <button onClick={() => exportFile('pdf')} disabled={exporting === 'pdf'} data-testid="export-pdf" className="inline-flex items-center gap-2 bg-[#0C2159] hover:bg-[#13317a] text-white text-sm font-medium px-4 py-2 rounded-xl disabled:opacity-60">
          {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF
        </button>
      </div>
      {!data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 gap-4 mb-4 max-w-md">
            <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="report-total-tickets"><p className="text-3xl font-bold text-[#0C2159]">{data.total_tickets}</p><p className="text-sm text-slate-500 mt-1">Tickets sold</p></div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="report-total-revenue"><p className="text-3xl font-bold text-[#0C2159]">{data.currency} {Number(data.total_revenue).toFixed(2)}</p><p className="text-sm text-slate-500 mt-1">Total revenue</p></div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
            {data.rows.length === 0 ? <Empty text="No sales in this period yet." /> : (
              <table className="w-full text-sm" data-testid="report-table">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left p-3">Period</th><th className="text-right p-3">Tickets Sold</th><th className="text-right p-3">Revenue ({data.currency})</th></tr></thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.period} className="border-t border-slate-100"><td className="p-3 font-medium">{r.period}</td><td className="p-3 text-right">{r.tickets}</td><td className="p-3 text-right">{Number(r.revenue).toFixed(2)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Settings ---------------- */
function SettingsView({ company, onSaved }) {
  const [f, setF] = useState({
    name: company.name || '', registration_number: company.registration_number || '',
    contact_number: company.contact_number || '', email: company.email || '',
    address: company.address || '', logo_url: company.logo_url || '',
    image_url: company.image_url || '', description: company.description || '',
    booking_banner_url: company.booking_banner_url || '',
    booking_banner_link: company.booking_banner_link || '',
    booking_banner_enabled: company.booking_banner_enabled || false,
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerErr, setBannerErr] = useState(false);
  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerUploading(true); setBannerErr(false); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/api/eticket/company/upload-image?slot=banner`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setF((prev) => ({ ...prev, booking_banner_url: data.url, booking_banner_enabled: true }));
      setMsg('Banner uploaded. Click "Save changes" to publish.');
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Upload failed. Please try a JPG/PNG under 8MB.');
    } finally { setBannerUploading(false); e.target.value = ''; }
  };
  const save = async () => {
    setBusy(true); setMsg('');
    try { await axios.put(`${API}/api/eticket/company`, f); setMsg('Saved.'); onSaved(); }
    catch (e) { setMsg(e.response?.data?.detail || 'Save failed.'); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="settings-view">
      <H title="Settings" sub="Company details" />
      <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2"><Building2 className="w-4 h-4" /> Company Details</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Inp label="Company name" v={f.name} on={e => setF({ ...f, name: e.target.value })} testid="set-name" />
          <Inp label="Registration number" v={f.registration_number} on={e => setF({ ...f, registration_number: e.target.value })} testid="set-reg" />
          <Inp label="Contact number" v={f.contact_number} on={e => setF({ ...f, contact_number: e.target.value })} testid="set-contact" />
          <Inp label="Email" v={f.email} on={e => setF({ ...f, email: e.target.value })} testid="set-email" />
          <Inp label="Logo URL (shown as small badge on hero)" v={f.logo_url} on={e => setF({ ...f, logo_url: e.target.value })} testid="set-logo" />
          <Inp label="Banner image URL (fallback if no Hero image uploaded below)" v={f.image_url} on={e => setF({ ...f, image_url: e.target.value })} testid="set-image" />
        </div>
        <p className="text-xs text-slate-500 mt-2">
          💡 <strong>Logo URL</strong> appears as a small circular badge in the top-left of your public booking page hero.
          <strong> Banner image URL</strong> is used as the hero background only if you have not uploaded a Hero image in the <em>Booking Page</em> section below.
        </p>
        <div className="mt-3 space-y-3">
          <Inp label="Address" v={f.address} on={e => setF({ ...f, address: e.target.value })} testid="set-address" />
          <Inp label="Description" v={f.description} on={e => setF({ ...f, description: e.target.value })} testid="set-desc" />
        </div>
        <div className="mt-5 pt-5 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-slate-900 text-sm flex items-center gap-2"><Tags className="w-4 h-4" /> Promotional Banner</h4>
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" data-testid="set-banner-enabled-label">
              <input type="checkbox" checked={f.booking_banner_enabled} onChange={e => setF({ ...f, booking_banner_enabled: e.target.checked })} data-testid="set-banner-enabled" className="w-4 h-4 accent-[#0C2159]" />
              {f.booking_banner_enabled ? 'Shown' : 'Hidden'}
            </label>
          </div>
          <p className="text-xs text-slate-500 mb-3">A promo image (e.g. partner offer) shown on your public booking page above the ticket selector. <strong>Upload a file</strong> for best results — pasted page links (ibb.co, kommodo.ai, Google Drive) are NOT direct images and will appear broken.</p>
          <div className="flex flex-wrap items-center gap-3">
            <label data-testid="set-banner-upload-label" className={`inline-flex items-center gap-2 cursor-pointer bg-[#0C2159] hover:bg-[#13317a] text-white font-semibold px-4 py-2.5 rounded-xl ${bannerUploading ? 'opacity-60 pointer-events-none' : ''}`}>
              {bannerUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {bannerUploading ? 'Uploading…' : 'Upload banner image'}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadBanner} data-testid="set-banner-file" className="hidden" />
            </label>
            {f.booking_banner_url && (
              <button type="button" onClick={() => setF({ ...f, booking_banner_url: '' })} data-testid="set-banner-clear" className="text-sm text-red-500 hover:text-red-700 font-medium">Remove</button>
            )}
          </div>
          <details className="mt-3">
            <summary className="text-xs text-slate-500 cursor-pointer">Advanced: paste a direct image URL instead</summary>
            <div className="mt-2 space-y-3">
              <Inp label="Banner image URL (must end in .jpg/.png/.webp)" v={f.booking_banner_url} on={e => { setF({ ...f, booking_banner_url: e.target.value }); setBannerErr(false); }} testid="set-banner-url" />
            </div>
          </details>
          <Inp label="Banner link (optional — where it goes when tapped)" v={f.booking_banner_link} on={e => setF({ ...f, booking_banner_link: e.target.value })} testid="set-banner-link" />
          {f.booking_banner_url && !bannerErr && (
            <img src={f.booking_banner_url} onError={() => setBannerErr(true)} alt="Banner preview" className="mt-3 w-full max-w-xs rounded-lg border border-slate-200" data-testid="set-banner-preview" />
          )}
          {f.booking_banner_url && bannerErr && (
            <p className="mt-3 text-xs font-semibold text-amber-600" data-testid="set-banner-broken">⚠️ This link isn't a direct image so it can't be displayed. Please use "Upload banner image" above instead.</p>
          )}
        </div>
        {msg && <p className="text-sm mt-3 text-slate-600" data-testid="set-msg">{msg}</p>}
        <button onClick={save} disabled={busy} data-testid="set-save" className="mt-4 bg-[#0C2159] hover:bg-[#13317a] text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save changes
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl mt-5">
        <SupplierInfoSettings company={company} onSaved={onSaved} />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl mt-5">
        <UsersSection />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl mt-5">
        <EmailSettings company={company} onSaved={onSaved} />
      </div>
      <div className="mt-5">
        <H title="Booking Page (Public)" sub="Customize how guests see your public booking page." />
        <BookingEngineSettings company={company} onSaved={onSaved} />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl mt-5">
        <h3 className="font-bold text-slate-900 mb-2">Telegram Notifications</h3>
        <p className="text-sm text-slate-500">Telegram alerts for new bookings are coming soon.</p>
      </div>
    </div>
  );
}

/* ---- Users management ---- */
function UsersSection() {
  const [users, setUsers] = useState(null);
  const [f, setF] = useState({ full_name: '', username: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = () => axios.get(`${API}/api/eticket/users`).then(r => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    setErr('');
    if (!f.email || !f.username || f.password.length < 8) { setErr('Email, username and a password (8+ chars) are required.'); return; }
    setBusy(true);
    try { await axios.post(`${API}/api/eticket/users`, f); setF({ full_name: '', username: '', email: '', password: '' }); await load(); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not add user.'); }
    finally { setBusy(false); }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.username} (min 8 chars):`);
    if (!pw) return;
    try { await axios.post(`${API}/api/eticket/users/${u.id}/password`, { password: pw }); alert('Password updated.'); }
    catch (e) { alert(e.response?.data?.detail || 'Failed.'); }
  };
  const del = async (u) => { if (window.confirm(`Delete user ${u.username}?`)) { await axios.delete(`${API}/api/eticket/users/${u.id}`); load(); } };
  return (
    <div data-testid="users-section">
      <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2"><Users className="w-4 h-4" /> Users</h3>
      <div className="space-y-2 mb-5" data-testid="users-list">
        {!users ? <Loading /> : users.map(u => (
          <div key={u.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3" data-testid={`user-row-${u.username}`}>
            <div>
              <p className="font-medium text-slate-800 text-sm">{u.full_name || u.username} <span className="text-xs text-slate-400">@{u.username}</span></p>
              <p className="text-xs text-slate-500">{u.email}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${u.role === 'owner' ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-600'}`}>{u.role}</span>
              {u.role !== 'owner' && <>
                <button onClick={() => resetPw(u)} title="Reset password" data-testid={`user-resetpw-${u.username}`} className="text-[#0C2159] hover:text-[#13317a]"><KeyRound className="w-4 h-4" /></button>
                <button onClick={() => del(u)} title="Delete user" data-testid={`user-delete-${u.username}`} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
              </>}
            </div>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="user-create-form">
        <Inp label="Full name" v={f.full_name} on={e => setF({ ...f, full_name: e.target.value })} testid="user-fullname" />
        <Inp label="Username *" v={f.username} on={e => setF({ ...f, username: e.target.value })} testid="user-username" />
        <Inp label="Email *" v={f.email} on={e => setF({ ...f, email: e.target.value })} testid="user-email" />
        <Inp label="Password * (8+)" type="password" v={f.password} on={e => setF({ ...f, password: e.target.value })} testid="user-password" />
      </div>
      {err && <p className="text-sm text-red-600 mt-2" data-testid="user-error">{err}</p>}
      <button onClick={add} disabled={busy} data-testid="user-add-btn" className="mt-3 bg-[#0C2159] hover:bg-[#13317a] text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add User
      </button>
    </div>
  );
}

/* ---- Email settings ---- */
function EmailSettings({ company, onSaved }) {
  const es = company.email_settings || {};
  const [f, setF] = useState({ sender: es.sender || '', cc: es.cc || '', bcc: es.bcc || '' });
  const [testTo, setTestTo] = useState(company.email || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const save = async () => {
    setBusy(true); setMsg('');
    try { await axios.put(`${API}/api/eticket/company`, { email_settings: f }); setMsg('Email settings saved.'); onSaved(); }
    catch (e) { setMsg(e.response?.data?.detail || 'Save failed.'); } finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!testTo) { setMsg('Enter a test recipient.'); return; }
    setBusy(true); setMsg('');
    try { const { data } = await axios.post(`${API}/api/eticket/test-email`, { to: testTo }); setMsg(data.sent ? 'Test email sent.' : 'Send failed.'); }
    catch (e) { setMsg(e.response?.data?.detail || 'Send failed.'); } finally { setBusy(false); }
  };
  return (
    <div data-testid="email-settings">
      <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2"><Mail className="w-4 h-4" /> Email Settings</h3>
      <div className="grid sm:grid-cols-3 gap-3">
        <Inp label="Sender / Reply-to email" v={f.sender} on={e => setF({ ...f, sender: e.target.value })} testid="email-sender" />
        <Inp label="CC email" v={f.cc} on={e => setF({ ...f, cc: e.target.value })} testid="email-cc" />
        <Inp label="BCC email" v={f.bcc} on={e => setF({ ...f, bcc: e.target.value })} testid="email-bcc" />
      </div>
      <button onClick={save} disabled={busy} data-testid="email-save" className="mt-3 bg-[#0C2159] hover:bg-[#13317a] text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save
      </button>
      <div className="mt-5 pt-4 border-t border-slate-100">
        <p className="text-xs font-medium text-slate-600 mb-1">Test email</p>
        <div className="flex gap-2 max-w-md">
          <input value={testTo} onChange={e => setTestTo(e.target.value)} data-testid="email-test-to" className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0C2159]" placeholder="recipient@email.com" />
          <button onClick={sendTest} disabled={busy} data-testid="email-test-send" className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 rounded-lg text-sm flex items-center gap-1.5"><Send className="w-4 h-4" /> Test</button>
        </div>
      </div>
      {msg && <p className="text-sm mt-3 text-slate-600" data-testid="email-msg">{msg}</p>}
    </div>
  );
}

/* ---------------- Shared bits ---------------- */
const H = ({ title, sub }) => (
  <div className="mb-6"><h2 className="text-2xl font-bold text-slate-900">{title}</h2>{sub && <p className="text-slate-500 text-sm mt-1">{sub}</p>}</div>
);
const Loading = () => <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#0C2159]" /></div>;
const Empty = ({ text }) => <div className="p-10 text-center text-slate-400 text-sm">{text}</div>;
const Inp = ({ label, v, on, type = 'text', testid }) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-600">{label}</span>
    <input data-testid={testid} type={type} value={v} onChange={on} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0C2159]" />
  </label>
);
const Row2 = ({ k, v }) => (
  <div className="flex justify-between border-b border-slate-100 py-2 text-sm"><span className="text-slate-500">{k}</span><span className="font-semibold text-slate-900">{v}</span></div>
);
